import { createSelector } from "@reduxjs/toolkit";
import customSort from "../../../../helpers/sortByProp";
import { selectSearchRequestQuery } from "../../search/domains/selectors/shared";
import { decode, PRICE_FACET_ID } from "../codec/urlCodec";
import { RANGE } from "../model/selection";

const emptyObject = {};
const emptyArray = [];

const getFacetCatalog = (state) => state.facets?.catalog ?? emptyArray;

const alphabetically = customSort((refinement) => refinement.refinementId);
const normalizeId = (id) => String(id);

const selectRefine = (state) => selectSearchRequestQuery(state).refine;
const selectSort = (state) => selectSearchRequestQuery(state).sort;

/**
 * Describes what the *current results* are refined by, so it reads the query
 * the API answered rather than the URL, which may already have moved on.
 */
const selectAppliedSelection = createSelector(
  [selectRefine, selectSort],
  (refine, sort) => decode({ refine, sort }).selection,
);

const getFacetLookup = createSelector([getFacetCatalog], (facets) =>
  facets.reduce((lookup, facet) => {
    lookup[facet.id] = {
      refinementName: facet.name,
      facetValues: facet.facetValues ?? emptyArray,
    };

    return lookup;
  }, {}),
);

/**
 * Walks the catalog rather than the selection, because analytics reports
 * refinements in the order the shopper saw them. A selection is stored
 * canonically (sorted by id as a string), which would otherwise send
 * `red, brown, gold` where the panel listed `black, blue, brown`.
 */
const buildSelections = (facetValues, values) => {
  const selected = new Set(values.map(normalizeId));

  return facetValues
    .filter(({ id }) => selected.has(normalizeId(id)))
    .map(({ id, name }) => ({ selectionId: id, selectionName: name }));
};

const buildRefinement = (facetLookup, facetId, entry) => {
  const matchingFacet = facetLookup[facetId] ?? emptyObject;
  const refinement = {
    refinementId: facetId,
    ...(matchingFacet.refinementName !== undefined
      ? { refinementName: matchingFacet.refinementName }
      : emptyObject),
  };

  if (facetId === PRICE_FACET_ID && entry.kind === RANGE) {
    return { ...refinement, min: entry.min, max: entry.max };
  }

  return {
    ...refinement,
    selections: buildSelections(
      matchingFacet.facetValues ?? emptyArray,
      entry.values ?? emptyArray,
    ),
  };
};

const buildSort = (sort) => ({
  refinementId: "sort",
  refinementName: "sort",
  selections: [{ selectionId: sort }],
});

const getAllAppliedRefinements = createSelector(
  [selectAppliedSelection, getFacetLookup],
  ({ facets, sort }, facetLookup) => {
    const refinements = Object.entries(facets)
      .map(([facetId, entry]) => buildRefinement(facetLookup, facetId, entry))
      .sort(alphabetically);

    if (sort) {
      refinements.push(buildSort(sort));
    }

    return refinements.length ? refinements : emptyArray;
  },
);

export default getAllAppliedRefinements;
